import { buildShareCode, buildTopicSharePath, type ShareText } from '@shared/share-link';

export const isWeChatBrowser = () => typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent);

/** 手机 / 平板才用系统分享面板；电脑上（包括 Windows 自带的分享窗口）直接复制链接更顺手 */
export const isMobileDevice = () =>
  typeof navigator !== 'undefined' &&
  (/Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent)));

export function topicShareUrl(topicId: string, allTopicIds: readonly string[]): string {
  return `${window.location.origin}${buildTopicSharePath(buildShareCode(topicId, allTopicIds))}`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(area);
    return ok;
  }
}

export type ShareOutcome = 'navigated' | 'shared' | 'copied' | 'failed';

/**
 * 分享一个链接：
 * - 微信里：网页调不起微信分享，先复制“文案 + 链接”（只有点击当下才允许写剪贴板），
 *   再跳到这个主题自己的分享页提示“点右上角 ··· 转发”，这样转出去的是这个主题的卡片，而不是首页；
 * - 手机浏览器：弹出系统分享面板（里面也有“复制链接”）；
 * - 电脑：直接复制链接。
 */
export async function shareLink(
  url: string,
  title: string,
  options: { navigateInWeChat?: boolean; text?: ShareText } = {},
): Promise<ShareOutcome> {
  if (options.navigateInWeChat && isWeChatBrowser()) {
    const copied = await copyText(options.text?.full ?? url);
    window.location.href = `${url}${url.includes('?') ? '&' : '?'}share=${copied ? 'copied' : '1'}`;
    return 'navigated';
  }
  if (isMobileDevice() && typeof navigator.share === 'function' && !isWeChatBrowser()) {
    try {
      await navigator.share({ title, text: options.text?.body, url });
      return 'shared';
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return 'shared';
    }
  }
  // 复制“文案 + 链接”，粘贴到微信里直接能发
  return (await copyText(options.text?.full ?? url)) ? 'copied' : 'failed';
}
